import * as React from 'react';
import { useTranslation } from 'next-i18next';
import { useAtomValue } from 'jotai';

import { Period, Variable } from '@restworld/utility-types';
import { BuildingOfficeIcon, ClockIcon, UserIcon } from '@heroicons/react/24/outline';
import { Heading } from '@restworld/ui-ds-base';
import { cn, formatGoogleOpeningHours } from '@restworld/utils-common';

import { companyProfileAtom } from '../../job-position/job-position.atoms';

export const CompanyServiceDetails: React.FunctionComponent = () => {
  const { clientTypes, restaurantTypes, openingHours = [] } = useAtomValue(companyProfileAtom);

  return <RestaurantWorkingHours {...{ clientTypes, restaurantTypes, openingHours }} />;
};

export type RestaurantWorkingHoursProps = {
  openingHours: Period[];
  clientTypes?: Variable[];
  restaurantTypes?: Variable[];
};

export const RestaurantWorkingHours = ({ openingHours, clientTypes, restaurantTypes }: RestaurantWorkingHoursProps) => {
  const { t } = useTranslation('common');
  return (
    <>
      {restaurantTypes?.length ? (
        <DataLabelComponent title={t('local_topology')} icon="BuildingOfficePurpleIcon" data={restaurantTypes} />
      ) : null}
      {clientTypes?.length ? (
        <DataLabelComponent title={t('customer_type')} icon="UserPurpleIcon" data={clientTypes} />
      ) : null}
      <RestaurantHours {...{ openingHours }} />
      {/* <ServiceOptions /> */}
    </>
  );
};

export default CompanyServiceDetails;
// --> components
const Wrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-2', className)} {...props} />
);
// --> icon map for DataLabelComponent
const dataLabelIconMap = {
  BuildingOfficePurpleIcon: <BuildingOfficeIcon className="h-5 text-primary-800" />,
  UserPurpleIcon: <UserIcon className="h-5 text-primary-800" />,
  ClockPurpleIcon: <ClockIcon className="h-5 text-primary-800" />,
};

// --> reuseable component
const DataLabelComponent = ({
  title,
  data,
  icon,
}: {
  title: string;
  data: Variable[];
  icon: 'BuildingOfficePurpleIcon' | 'UserPurpleIcon' | 'ClockPurpleIcon';
}) => (
  <Wrapper>
    <span className="flex flex-row items-center gap-1.5">
      {dataLabelIconMap[icon]}
      <Heading>{title}</Heading>
    </span>
    <Heading weight="normal" color="muted">
      {`${data.map(({ label }: { label: string }) => ` ${label}`)}`}
    </Heading>
  </Wrapper>
);

// ---> restaurant public hours
export const RestaurantHours = ({ openingHours }: Pick<RestaurantWorkingHoursProps, 'openingHours'>) => {
  const { t } = useTranslation('common');
  return openingHours.length ? (
    <Wrapper>
      <span className="flex flex-row items-center gap-1.5">
        <ClockIcon className="h-5 text-primary-800" />
        <Heading>{t('public_opening_hours')}</Heading>
      </span>
      <FormattedGoogleRestaurantHours openingHours={openingHours} />
    </Wrapper>
  ) : null;
};

const FormattedGoogleRestaurantHours = ({ openingHours }: Pick<RestaurantWorkingHoursProps, 'openingHours'>) => {
  const { t } = useTranslation('common');
  const workingHours = React.useMemo(() => formatGoogleOpeningHours(openingHours), [openingHours]);

  return React.useMemo(
    () => (
      <>
        {workingHours.map((hours, index) => {
          const [weekday, interval] = hours.split(': ');
          return (
            <RestaurantHoursListWrapper key={index}>
              <RestaurantHoursItem>
                <Heading>{t(weekday)}</Heading>
                <Heading weight="light" color="muted">
                  {interval === 'Closed' ? t('closed') : interval}
                </Heading>
              </RestaurantHoursItem>
            </RestaurantHoursListWrapper>
          );
        })}
      </>
    ),
    [t, workingHours]
  );
};
const RestaurantHoursListWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <ul className={cn('flex flex-col gap-2 w-full', className)} {...props} />
);
const RestaurantHoursItem = ({ className, ...props }: React.HTMLAttributes<HTMLLIElement>) => (
  <li className={cn('flex justify-between tracking-normal ; > div:last-child { span { } }', className)} {...props} />
);
